import { useEffect, useState, useCallback } from "react";
import {
  ListChecks, Plus, Check, X, Play, Flag, Trash2, ShieldCheck, ShieldAlert, Cpu, ChevronDown,
} from "lucide-react";
import {
  listJobs, createJob, approveJob, rejectJob, runJob, completeJob, deleteJob,
  listAgents, listClients,
} from "@/api/entities";
import { Panel, Label, Heading, Loading, Pill, HudButton, Empty, accent } from "@/components/hud";
import { useOperator } from "@/context/OperatorContext";
import { fmtDateTime } from "@/lib/format";
import { toast } from "sonner";

const STATES = ["all", "queued", "approved", "running", "review", "done", "rejected"];
const STATE_TONE = { queued: "amber", approved: "cyan", running: "violet", review: "magenta", done: "mint", rejected: "magenta" };

export default function Jobs() {
  const { canWrite } = useOperator();
  const [jobs, setJobs] = useState([]);
  const [agents, setAgents] = useState([]);
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");
  const [creating, setCreating] = useState(false);
  const [busy, setBusy] = useState(null);

  const load = useCallback(async () => {
    try {
      const [j, a, c] = await Promise.all([listJobs(), listAgents(), listClients()]);
      setJobs(j.items || j || []);
      setAgents(a.items || a || []);
      setClients(c.items || c || []);
    } catch {
      toast.error("Could not load jobs");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const act = async (fn, id, okMsg) => {
    setBusy(id);
    try {
      await fn();
      toast.success(okMsg);
      await load();
    } catch (e) {
      const msg = e?.response?.data?.detail || "Action needs operator sign-in";
      toast.error(typeof msg === "string" ? msg : "Action failed");
    } finally {
      setBusy(null);
    }
  };

  const save = async (form) => {
    try {
      await createJob(form);
      toast.success("Job queued for approval");
      setCreating(false);
      load();
    } catch {
      toast.error("Creating a job needs operator sign-in");
    }
  };

  if (loading) return <Loading label="Loading approval queue…" />;

  const shown = filter === "all" ? jobs : jobs.filter((j) => j.state === filter);
  const awaiting = jobs.filter((j) => j.state === "queued").length;

  return (
    <div className="space-y-6 yb-rise" data-testid="jobs-page">
      <div className="flex items-end justify-between gap-3 flex-wrap">
        <div>
          <Label>Jobs · approval gate</Label>
          <h1 className="font-display text-[26px] sm:text-[32px] mt-1.5" style={{ color: "var(--yb-text)" }}>
            Approval Queue
          </h1>
        </div>
        {canWrite && <HudButton onClick={() => setCreating(true)}><Plus size={15} /> New job</HudButton>}
      </div>

      <Panel className="flex items-start gap-3">
        <ShieldCheck size={18} style={{ color: "var(--yb-mint)" }} className="shrink-0 mt-0.5" />
        <p className="text-[12px]" style={{ color: "var(--yb-text-muted)" }}>
          <span style={{ color: "var(--yb-text-dim)" }}>{awaiting} job{awaiting === 1 ? "" : "s"} awaiting approval.</span>{" "}
          An agent job <strong>cannot run</strong> until a human operator approves it — the server refuses any
          run request without a recorded <code className="font-mono">approved_by</code>.
        </p>
      </Panel>

      {/* filter chips */}
      <div className="flex items-center gap-2 flex-wrap">
        {STATES.map((s) => (
          <button key={s} onClick={() => setFilter(s)}
            className="pill"
            style={{
              color: filter === s ? "#04101b" : accent(STATE_TONE[s] || "cyan").c,
              background: filter === s ? accent(STATE_TONE[s] || "cyan").c : "transparent",
            }}>
            {s}{s !== "all" ? ` · ${jobs.filter((j) => j.state === s).length}` : ` · ${jobs.length}`}
          </button>
        ))}
      </div>

      {shown.length === 0 ? (
        <Panel><Empty icon={ListChecks} title="Nothing here"
          hint={filter === "all" ? "Create a job to assign work to an agent." : `No jobs in “${filter}”.`} /></Panel>
      ) : (
        <div className="space-y-3">
          {shown.map((j) => (
            <JobCard key={j.id} job={j} canWrite={canWrite} busy={busy === j.id}
              onApprove={() => act(() => approveJob(j.id, "BASHAM"), j.id, "Approved")}
              onReject={() => act(() => rejectJob(j.id), j.id, "Rejected")}
              onRun={() => act(() => runJob(j.id), j.id, "Agent run complete — draft ready")}
              onComplete={() => act(() => completeJob(j.id), j.id, "Marked done")}
              onDelete={() => act(() => deleteJob(j.id), j.id, "Deleted")}
            />
          ))}
        </div>
      )}

      {creating && <JobModal agents={agents} clients={clients} onSave={save} onClose={() => setCreating(false)} />}
    </div>
  );
}

function JobCard({ job, canWrite, busy, onApprove, onReject, onRun, onComplete, onDelete }) {
  const [open, setOpen] = useState(false);
  const tone = STATE_TONE[job.state] || "cyan";
  const approved = !!job.approved_by;

  return (
    <Panel>
      <div className="flex items-start gap-3">
        <span className="w-9 h-9 rounded flex items-center justify-center shrink-0"
              style={{ border: `1px solid ${accent(tone).c}`, color: accent(tone).c }}>
          <Cpu size={16} />
        </span>
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-[14px]" style={{ color: "var(--yb-text)" }}>{job.title}</span>
            <span className="pill" style={{ color: accent(tone).c }}>{job.state}</span>
          </div>
          <div className="font-mono text-[11px] mt-1" style={{ color: "var(--yb-text-muted)" }}>
            {job.agent_name} · {job.client_name || "internal"} · {fmtDateTime(job.created_date)}
          </div>
          <div className="mt-1.5 flex items-center gap-1.5 font-mono text-[11px]"
               style={{ color: approved ? "var(--yb-mint)" : "var(--yb-amber)" }}>
            {approved ? <ShieldCheck size={12} /> : <ShieldAlert size={12} />}
            {approved ? `approved by ${job.approved_by}` : "not yet approved"}
          </div>
        </div>
        {(job.brief || job.output) && (
          <button onClick={() => setOpen(!open)} aria-label="Toggle details"
            className="w-8 h-8 rounded flex items-center justify-center hover:bg-white/5 shrink-0"
            style={{ border: "1px solid var(--yb-hairline)", color: "var(--yb-text-muted)" }}>
            <ChevronDown size={15} style={{ transform: open ? "rotate(180deg)" : "none", transition: "transform .2s" }} />
          </button>
        )}
      </div>

      {open && (
        <div className="mt-3 pt-3 space-y-3" style={{ borderTop: "1px solid var(--yb-hairline)" }}>
          {job.brief && (
            <div>
              <Label>Brief</Label>
              <p className="text-[12.5px] mt-1" style={{ color: "var(--yb-text-dim)" }}>{job.brief}</p>
            </div>
          )}
          {job.output && (
            <div>
              <Label accent="mint">Agent draft {job.output_template ? "· template fallback" : "· AI"}</Label>
              <pre className="text-[12px] mt-1 whitespace-pre-wrap font-mono p-3 rounded"
                   style={{ color: "var(--yb-text-dim)", background: "var(--yb-panel-2)", border: "1px solid var(--yb-hairline)" }}>
                {job.output}
              </pre>
              <div className="font-mono text-[10px] mt-1.5" style={{ color: "var(--yb-text-muted)" }}>
                ▲ draft only — nothing was sent, published, or charged.
              </div>
            </div>
          )}
        </div>
      )}

      {canWrite && (
        <div className="flex items-center gap-2 mt-3 flex-wrap">
          {job.state === "queued" && (
            <>
              <HudButton tone="mint" onClick={onApprove} disabled={busy} className="h-8" data-testid={`job-approve-${job.id}`}><Check size={14} /> Approve</HudButton>
              <HudButton tone="magenta" variant="ghost" onClick={onReject} disabled={busy} className="h-8"><X size={14} /> Reject</HudButton>
            </>
          )}
          {job.state === "approved" && (
            <HudButton tone="violet" onClick={onRun} disabled={busy} className="h-8">
              <Play size={14} /> {busy ? "Running…" : "Run agent"}
            </HudButton>
          )}
          {(job.state === "running" || job.state === "review") && (
            <HudButton tone="mint" onClick={onComplete} disabled={busy} className="h-8"><Flag size={14} /> Mark done</HudButton>
          )}
          <button onClick={onDelete} disabled={busy} aria-label="Delete job"
            className="w-8 h-8 rounded flex items-center justify-center hover:bg-white/5 ml-auto"
            style={{ border: "1px solid var(--yb-hairline)", color: "var(--yb-magenta)" }}>
            <Trash2 size={14} />
          </button>
        </div>
      )}
    </Panel>
  );
}

function JobModal({ agents, clients, onSave, onClose }) {
  const [f, setF] = useState({ title: "", agent_id: agents[0]?.id || "", client_id: "", brief: "" });
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const submit = (e) => {
    e.preventDefault();
    const agent = agents.find((a) => a.id === f.agent_id);
    const client = clients.find((c) => c.id === f.client_id);
    onSave({
      title: f.title.trim(),
      agent_id: f.agent_id,
      agent_name: agent?.name,
      client_id: f.client_id || null,
      client_name: client?.name || null,
      brief: f.brief.trim(),
    });
  };
  const inputCls = "w-full h-10 px-3 rounded text-[13px] bg-transparent outline-none";
  const inputStyle = { border: "1px solid var(--yb-hairline)", color: "var(--yb-text)" };

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center p-4" style={{ background: "rgba(3,6,15,0.78)" }} onClick={onClose}>
      <form onSubmit={submit} onClick={(e) => e.stopPropagation()} className="hud-panel w-full max-w-md p-6"
            role="dialog" aria-modal="true" aria-label="New job">
        <div className="flex items-center justify-between mb-4">
          <span className="font-display text-[16px]" style={{ color: "var(--yb-cyan)" }}>New job</span>
          <button type="button" onClick={onClose} aria-label="Close"
                  className="w-8 h-8 rounded flex items-center justify-center hover:bg-white/5"
                  style={{ border: "1px solid var(--yb-hairline)", color: "var(--yb-text-muted)" }}><X size={16} /></button>
        </div>
        <div className="space-y-3">
          <label className="block">
            <span className="font-mono text-[10px] uppercase tracking-[0.18em]" style={{ color: "var(--yb-text-muted)" }}>Title</span>
            <input className={`${inputCls} mt-1`} style={inputStyle} value={f.title} onChange={set("title")} required autoFocus />
          </label>
          <div className="grid grid-cols-2 gap-3">
            <label className="block">
              <span className="font-mono text-[10px] uppercase tracking-[0.18em]" style={{ color: "var(--yb-text-muted)" }}>Agent</span>
              <select className={`${inputCls} mt-1`} style={inputStyle} value={f.agent_id} onChange={set("agent_id")}>
                {agents.map((a) => <option key={a.id} value={a.id}>{a.name}</option>)}
              </select>
            </label>
            <label className="block">
              <span className="font-mono text-[10px] uppercase tracking-[0.18em]" style={{ color: "var(--yb-text-muted)" }}>Client</span>
              <select className={`${inputCls} mt-1`} style={inputStyle} value={f.client_id} onChange={set("client_id")}>
                <option value="">— internal —</option>
                {clients.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
              </select>
            </label>
          </div>
          <label className="block">
            <span className="font-mono text-[10px] uppercase tracking-[0.18em]" style={{ color: "var(--yb-text-muted)" }}>Brief</span>
            <textarea className="w-full mt-1 px-3 py-2 rounded text-[13px] bg-transparent outline-none" rows={3}
                      style={inputStyle} value={f.brief} onChange={set("brief")} placeholder="What should this agent prepare?" />
          </label>
          <p className="font-mono text-[10px]" style={{ color: "var(--yb-text-muted)" }}>
            New jobs enter the queue as <span style={{ color: "var(--yb-amber)" }}>queued</span> and stay there until you approve them.
          </p>
        </div>
        <div className="flex gap-2 pt-4">
          <HudButton type="submit" disabled={!f.title.trim()}>Queue job</HudButton>
          <HudButton type="button" variant="ghost" onClick={onClose}>Cancel</HudButton>
        </div>
      </form>
    </div>
  );
}
